import { create } from 'zustand';
import axios from 'axios';
import { getDeviceId } from '../../lib/deviceId';
import { getOpenWindow, submitCheckin } from './attendanceApi';
import type { Checkin, OpenWindowInfo } from './attendanceApi';

// Student-side attendance state for /class (BP 23). Polls GET /open every
// 10s while CheckInCard is mounted — the same "open-window poll" §6 of
// docs/IMPLEMENTATION-PLAN-22-27.md specifies. Nothing here ever reads or
// invents a timestamp/slot to send to the server; codes go up exactly as
// typed and the server is the sole judge.

export interface CheckinErrorState {
  code: string;
  message: string;
}

interface AttendanceState {
  window: OpenWindowInfo | null;
  loading: boolean;
  loadError: boolean;
  submitting: boolean;
  submitError: CheckinErrorState | null;
  lastCheckin: Checkin | null;
  pollHandle: ReturnType<typeof setInterval> | null;

  refresh: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  submitCode: (code: string) => Promise<boolean>;
  clearSubmitError: () => void;
}

const POLL_MS = 10_000;

function messageForCheckinError(err: unknown): CheckinErrorState {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    const code = data?.error || 'unknown_error';
    const MESSAGES: Record<string, string> = {
      code_invalid: 'That code is not valid — check the projected code and try again.',
      window_closed: 'This attendance window has closed.',
      window_not_found: 'This window is no longer available.',
      too_many_attempts: 'Too many attempts for this window. Ask your instructor for a manual check-in.',
      device_already_used: 'This device already checked in a different student for this window.',
      unknown_device: 'This device has not been used by your account before. Ask your instructor for a manual check-in.',
      password_change_required: 'Change your password before you can check in.',
      invalid_body: 'Enter the 6-digit code shown by your instructor.',
      rate_limited: 'Too many attempts from this network. Please wait a moment.',
    };
    return { code, message: data?.message || MESSAGES[code] || 'Check-in failed. Please try again.' };
  }
  return { code: 'unknown_error', message: 'Check-in failed. Please try again.' };
}

export const useAttendanceStore = create<AttendanceState>((set, get) => ({
  window: null,
  loading: false,
  loadError: false,
  submitting: false,
  submitError: null,
  lastCheckin: null,
  pollHandle: null,

  refresh: async () => {
    set({ loading: true });
    try {
      const win = await getOpenWindow();
      set({ window: win, loading: false, loadError: false });
    } catch {
      set({ loading: false, loadError: true });
    }
  },

  startPolling: () => {
    if (get().pollHandle) return;
    void get().refresh();
    const handle = setInterval(() => { void get().refresh(); }, POLL_MS);
    set({ pollHandle: handle });
  },

  stopPolling: () => {
    const h = get().pollHandle;
    if (h) clearInterval(h);
    set({ pollHandle: null });
  },

  submitCode: async (code: string) => {
    const win = get().window;
    if (!win) return false;
    set({ submitting: true, submitError: null });
    try {
      const checkin = await submitCheckin({ window_id: win.id, code, device_id: getDeviceId() });
      set({ submitting: false, lastCheckin: checkin });
      await get().refresh();
      return true;
    } catch (err) {
      set({ submitting: false, submitError: messageForCheckinError(err) });
      return false;
    }
  },

  clearSubmitError: () => set({ submitError: null }),
}));
